"use client";

/**
 * What the scanned page already has.
 *
 * This used to be five paragraphs of prose and it read as a wall. Nobody reads a
 * wall at the exact moment they are waiting to get on with the task, so the
 * findings are now three numbers you can take in at a glance, one verdict line,
 * and the type list folded away.
 *
 * The rule applied here and worth keeping: a number plus a two-word label beats
 * a sentence containing the same number. Sentences are for things a number
 * cannot say.
 */

import React from "react";
import { AlertTriangle, ArrowDown, Check, Link2, Link2Off } from "lucide-react";
import * as s from "./styles";

type Props = {
  url: string;
  existing: {
    types: string[];
    hasGraph: boolean;
    hasIds: boolean;
    isLinked: boolean;
    brokenBlocks: number;
    blockCount: number;
  };
  microdataFound: boolean;
  rdfaFound: boolean;
  /** How many form fields we managed to prefill. */
  prefilledCount: number;
  suggestedTypes: string[];
};

function hostOf(url: string): string {
  try {
    return new URL(url).host.replace(/^www\./, "");
  } catch {
    return url;
  }
}

function Stat({
  value,
  label,
  tone = "ink",
}: {
  value: number;
  label: string;
  tone?: "ink" | "good" | "bad";
}) {
  return (
    <div>
      <span
        className={`block font-serif text-[38px] font-medium italic leading-none tracking-[-0.02em] ${
          tone === "bad" ? "text-red-700" : tone === "good" ? "text-emerald-700" : "text-nws-ink"
        }`}
      >
        {value}
      </span>
      <span className="mt-1.5 block text-[12px] font-semibold uppercase tracking-[0.12em] text-nws-ink/40">
        {label}
      </span>
    </div>
  );
}

export default function ExistingSchemaReport({
  url,
  existing,
  microdataFound,
  rdfaFound,
  prefilledCount,
  suggestedTypes,
}: Props) {
  const { types, blockCount, brokenBlocks, isLinked } = existing;
  const hasAny = blockCount > 0;

  // At most one verdict line. Ranked worst first, because the thing most worth
  // knowing is the thing most wrong.
  const verdict = brokenBlocks
    ? {
        icon: AlertTriangle,
        tone: "text-red-700",
        text: `${brokenBlocks} block${brokenBlocks === 1 ? " is" : "s are"} malformed, so ${brokenBlocks === 1 ? "it is" : "they are"} currently saying nothing.`,
      }
    : !hasAny
      ? {
          icon: AlertTriangle,
          tone: "text-amber-700",
          text: "No structured data at all, so everything you build below is a net gain.",
        }
      : !isLinked
        ? {
            icon: Link2Off,
            tone: "text-amber-700",
            text: "Nothing is linked by @id. Each block stands alone, which is the most common gap we see.",
          }
        : {
            icon: Link2,
            tone: "text-emerald-700",
            text: "Already linked by @id, which puts this page ahead of most sites.",
          };

  const VerdictIcon = verdict.icon;

  return (
    <div className={`overflow-hidden ${s.card}`}>
      <div className="p-5 sm:p-6">
        <p className="text-[12px] font-semibold uppercase tracking-[0.16em] text-nws-ink/40">
          <span className="font-serif text-[13px] font-medium normal-case italic tracking-normal text-nws-blue-dark">
            Scanned
          </span>{" "}
          {hostOf(url)}
        </p>

        <div className="mt-5 flex flex-wrap items-start gap-x-10 gap-y-5 sm:gap-x-14">
          <Stat value={blockCount} label={blockCount === 1 ? "block found" : "blocks found"} />
          <Stat value={prefilledCount} label="fields filled" tone={prefilledCount ? "good" : "ink"} />
          <Stat
            value={brokenBlocks}
            label={brokenBlocks === 1 ? "block broken" : "blocks broken"}
            tone={brokenBlocks ? "bad" : "ink"}
          />
        </div>

        <p className="mt-5 flex items-start gap-2.5 text-[14px] leading-relaxed text-nws-ink">
          <VerdictIcon
            className={`mt-0.5 h-4 w-4 shrink-0 ${verdict.tone}`}
            aria-hidden="true"
          />
          {verdict.text}
        </p>

        {(microdataFound || rdfaFound) && (
          <p className="mt-2 flex items-start gap-2.5 text-[13.5px] leading-relaxed text-nws-grey-text">
            <AlertTriangle
              className="mt-0.5 h-4 w-4 shrink-0 text-amber-700"
              aria-hidden="true"
            />
            Also uses {microdataFound ? "microdata" : "RDFa"}, an older style. Running both
            invites contradictions.
          </p>
        )}
      </div>

      {/* Detail, folded. Nobody needs eighteen type names to decide what to do next. */}
      {(types.length > 0 || suggestedTypes.length > 0) && (
        <div className="border-t border-nws-grey-medium bg-nws-grey-light px-5 py-3.5 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            {types.length > 0 ? (
              <details className="group min-w-0">
                <summary className={`cursor-pointer list-none ${s.btnText}`}>
                  See the {types.length} types already on the page
                  <span className="transition-transform group-open:rotate-45" aria-hidden="true">
                    +
                  </span>
                </summary>
                <p className="mt-2.5 max-w-[640px] text-[13px] leading-relaxed text-nws-grey-text">
                  {types.join(", ")}
                </p>
              </details>
            ) : (
              <span />
            )}

          </div>
        </div>
      )}
    </div>
  );
}
